import styles from "./Title.module.scss";
import { OwnBrand } from "@/utils/product/getOwnBrand";
import { ElementType, useRef, useEffect } from "react";
import classNames from "classnames/bind";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

const cx = classNames.bind(styles);
type Props = {
  /**
   * The title of the product.
   */
  title: string;
  /**
   * The own brand of the product.
   */
  ownBrand: OwnBrand;
  /**
   * Any other brand of the product.
   */
  otherBrand: string;
  /**
   * Used to override the element used to render the component.
   */
  element?: ElementType;
  /**
   * Used to remove h1 margin for beauty simplification work
   */
  simplifiedBeautyTitle?: boolean;
  /**
   * Boolean that represents if Responsive styling should be used
   */
  isResponsiveStylesEnabled?: boolean;
  /**
   * Boolean that represents if the brand title should be linked
   */
  linkBrandTitle?: boolean;
  /**
   * Boolean that represents if the product title should be reduced in font size
   */
  reducedProductTitleFont?: boolean;
};

export function TitleUI({
  title,
  ownBrand,
  otherBrand,
  element = "h1",
  simplifiedBeautyTitle = false,
  isResponsiveStylesEnabled,
  linkBrandTitle = false,
  reducedProductTitleFont = false,
}: Props) {
  const Component = element;

  const headerClassNames = cx(styles.title, {
    simplifiedBeautyTitle: simplifiedBeautyTitle,
    titleResponsive: isResponsiveStylesEnabled,
  });

  const isMounted = useRef(false);

  useEffect(() => {
    if (!linkBrandTitle) {
      return;
    }
    const breadcrumbs = document.querySelector('[data-testid="breadcrumbs"]');
    const branBreadcrumbLink = breadcrumbs?.querySelector('a[href^="/brand/"]');
    const brandLink = document.getElementById("pdp-brand-link");
    if (branBreadcrumbLink) {
      brandLink?.setAttribute(
        "href",
        branBreadcrumbLink.getAttribute("href") || "/",
      );
    } else {
      const lastLink = document.querySelector(
        '[data-testid="breadcrumbs"] a:last-of-type',
      );
      if (lastLink) {
        brandLink?.setAttribute("href", lastLink.getAttribute("href") || "/");
      } else {
        brandLink?.setAttribute("href", "/");
      }
    }

    brandLink?.classList.add(styles.brandLink ?? "");
  }, []);

  const brandTitleClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    dispatchMonetateQEvent("WPDP | Brand Title | Click");
    if (!linkBrandTitle) {
      e.preventDefault();
      return;
    }
  };
  return (
    <>
      <Component
        data-testid="product:title"
        className={headerClassNames}
        data-component="Title"
      >
        <div>
          {ownBrand === "jl_own_brand_anyday_legacy" && (
            <a
              className={styles.logoAnchor}
              href="/"
              id="pdp-brand-link"
              onClick={brandTitleClickHandler}
            >
              <span
                data-testid="product:title:anyday"
                className={cx(styles.anydayLogo, {
                  anydayLogoResponsive: isResponsiveStylesEnabled,
                })}
              >
                ANYDAY
              </span>
            </a>
          )}

          {ownBrand === "jl_own_brand_anyday" && (
            <a
              className={styles.logoAnchor}
              href="/"
              id="pdp-brand-link"
              onClick={brandTitleClickHandler}
            >
              <span
                data-testid="product:title:anyday"
                className={cx(styles.anydayLogo, {
                  anydayLogoResponsive: isResponsiveStylesEnabled,
                })}
              >
                <span className={styles.titlePrefix}>{`John Lewis `}</span>
                ANYDAY
              </span>
            </a>
          )}

          {ownBrand === "jl_own_brand_john_lewis" && (
            <a
              className={styles.logoAnchor}
              href="/"
              id="pdp-brand-link"
              onClick={brandTitleClickHandler}
            >
              <span
                data-testid="product:title:johnlewis"
                className={cx(styles.johnLewisBrand, {
                  logoResponsive: isResponsiveStylesEnabled,
                })}
              >
                John Lewis
              </span>
            </a>
          )}

          {!ownBrand && otherBrand && (
            <a
              className={styles.logoAnchor}
              id="pdp-brand-link"
              onClick={brandTitleClickHandler}
            >
              <span
                data-testid="product:title:otherBrand"
                className={cx(styles.otherBrand, {
                  logoResponsive: isResponsiveStylesEnabled,
                })}
              >
                {otherBrand}
              </span>
            </a>
          )}
          <span
            data-testid="product:title:content"
            className={cx({
              reducedProductTitleFont: reducedProductTitleFont && !!otherBrand,
            })}
          >
            {title}
          </span>
        </div>
      </Component>
    </>
  );
}
